/* global React, Icon, Card, KPI, fmt */
// ============================================================
// PLUMME 3 — Privat økonomi · «I dag» og Oversikt
// Designet fra Plumme-designsystemet. Helsescore, drivere og
// nøkkeltall kommer fra readmodel (getPersonalFinanceOverview).
// Proaktive innsikter («I dag») har ingen backend ennå og rendres
// i tom tilstand til innsiktsmotoren er koblet på.
// ============================================================
const { useEffect: povUseEffect, useState: povUseState } = React;

const povAvatar = () => (window.__resources && window.__resources.superPlummaHero) || 'src/img/SuperPlumma.png';
const POV_STATUS_COLOR = { amber: 'var(--warning)', green: 'var(--success)', red: 'var(--error)' };
// Nøklene må matche `kind` fra buildPersonalFinanceInsights i backend.
const POV_TODAY_TONE = {
  innsikt: { bg: 'var(--info-bg)', color: 'var(--plum-600)', get label() { return t('pkn.ov.tone.insight'); } },
  milepael: { bg: 'var(--success-bg)', color: 'var(--success)', get label() { return t('pkn.ov.tone.milestone'); } },
  mulighet: { bg: 'var(--plum-100)', color: 'var(--plum-700)', get label() { return t('pkn.ov.tone.opportunity'); } },
  positivt: { bg: 'var(--success-bg)', color: 'var(--success)', get label() { return t('pkn.ov.tone.milestone'); } },
  skatt: { bg: 'var(--info-bg)', color: 'var(--plum-600)', get label() { return t('pkn.ov.tone.tax'); } },
  spar: { bg: 'var(--success-bg)', color: 'var(--success)', get label() { return t('pkn.ov.tone.save'); } },
  varsel: { bg: 'var(--error-bg)', color: 'var(--error)', get label() { return t('pkn.ov.tone.alert'); } },
};
const POV_RANK = { innsikt: 3, milepael: 4, mulighet: 1, positivt: 4, skatt: 3, spar: 2, varsel: 0 };

// ---------------- Delte visuelle byggeklosser ----------------
function PoScoreRing({ score }) {
  const value = Math.max(0, Math.min(100, Number(score || 0)));
  const radius = 94;
  const circumference = 2 * Math.PI * radius;
  const color = value >= 80 ? 'var(--success)' : value >= 60 ? 'var(--warning)' : 'var(--error)';
  const verdict = value >= 80 ? t('pkn.ov.verdict.healthy') : value >= 60 ? t('pkn.ov.verdict.stable') : t('pkn.ov.verdict.needsFollowUp');
  const badgeBg = value >= 80 ? 'var(--success-bg)' : value >= 60 ? 'var(--warning-bg)' : 'var(--error-bg)';
  return <div className="aii-ring-wrap">
    <svg width="232" height="232" viewBox="0 0 232 232">
      <circle cx="116" cy="116" r={radius} fill="none" stroke="var(--panel-2)" strokeWidth="18" />
      <circle cx="116" cy="116" r={radius} fill="none" stroke={color} strokeWidth="18" strokeLinecap="round"
        strokeDasharray={`${circumference * (value / 100)} ${circumference}`} transform="rotate(-90 116 116)" />
      <text x="116" y="112" textAnchor="middle" fontSize="56" fontWeight="900" fill="var(--ink)">{value}</text>
      <text x="116" y="140" textAnchor="middle" fontSize="16" fontWeight="800" fill="var(--muted)">{t('sharedText.po.outOf100')}</text>
    </svg>
    <span className="aii-ring-badge" style={{ background: badgeBg, color }}><Icon name="sparkle" size={15} />{verdict}</span>
  </div>;
}

function PoSpark({ color, points }) {
  if (!points || points.length < 2) return null;
  const width = 176;
  const height = 42;
  const pad = 5;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = (max - min) || 1;
  const step = (width - pad * 2) / (points.length - 1);
  const coords = points.map((value, index) => [pad + index * step, height - pad - ((value - min) / range) * (height - pad * 2)]);
  const line = coords.map((point, index) => `${index ? 'L' : 'M'}${point[0].toFixed(1)} ${point[1].toFixed(1)}`).join(' ');
  const area = `${line} L ${coords[coords.length - 1][0].toFixed(1)} ${height} L ${coords[0][0].toFixed(1)} ${height} Z`;
  const last = coords[coords.length - 1];
  const gradientId = `posp${Math.round(min + max)}`;
  return <svg className="po-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
    <defs><linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.18" /><stop offset="100%" stopColor={color} stopOpacity="0" /></linearGradient></defs>
    <path d={area} fill={`url(#${gradientId})`} />
    <path d={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx={last[0]} cy={last[1]} r="3.5" fill={color} />
  </svg>;
}

function PlummaSays({ action, children, onAction }) {
  return <div className="pl-says">
    <span className="pl-says-ava"><img src={povAvatar()} alt="SuperPlumma" /></span>
    <div className="pl-says-body">
      <p>{children}</p>
      {action && <button className="pl-item-act" style={{ marginTop: 8 }} onClick={onAction}>{action}<Icon name="arrowRight" size={14} /></button>}
    </div>
  </div>;
}

// ---------------- «I dag» ----------------
function PoToday({ focus = null, health = null, insights = [], onAsk, onGoTab, onStatus }) {
  const statusFromInsights = () => Object.fromEntries(insights.filter((card) => card.status && card.status !== 'new').map((card) => [card.id, card.status]));
  const [done, setDone] = povUseState(statusFromInsights);
  const [leaving, setLeaving] = povUseState({});
  const [showAll, setShowAll] = povUseState(false);
  povUseEffect(() => { setDone(statusFromInsights()); }, [insights]);
  const complete = (card, state, snoozedUntil = '') => {
    setLeaving((current) => ({ ...current, [card.id]: true }));
    setTimeout(() => {
      setDone((current) => ({ ...current, [card.id]: state }));
      setLeaving((current) => { const next = { ...current }; delete next[card.id]; return next; });
      onStatus?.(card, state, snoozedUntil);
    }, 420);
  };
  const uncomplete = (card) => {
    setLeaving((current) => { const next = { ...current }; delete next[card.id]; return next; });
    setDone((current) => { const next = { ...current }; delete next[card.id]; return next; });
    onStatus?.(card, 'new');
  };
  const act = (card, action) => {
    if (action.go) onGoTab?.(action.go);
    if (action.chat) onAsk?.();
    if (action.approve) complete(card, 'done');
    else if (action.dismiss) complete(card, 'dismissed');
  };

  const ordered = [...insights].sort((a, b) => (POV_RANK[a.kind] ?? 9) - (POV_RANK[b.kind] ?? 9));
  const activeCards = ordered.filter((card) => !done[card.id]);
  const visibleCards = showAll ? activeCards : activeCards.slice(0, 3);
  const doneCards = ordered.filter((card) => done[card.id] && done[card.id] !== 'snoozed');
  const remaining = activeCards.filter((card) => !leaving[card.id]).length;
  const topId = (activeCards.find((card) => !leaving[card.id]) || {}).id;

  const savings = insights.filter((card) => card.save);
  const saveSum = savings.reduce((sum, card) => sum + card.save, 0);
  const alerts = insights.filter((card) => card.kind === 'varsel');
  const stats = [
    { color: health?.score >= 80 ? 'var(--success)' : health?.score >= 60 ? 'var(--warning)' : 'var(--error)', key: 'health', label: t('pkn.ov.stat.finances'), value: health?.label || t('pkn.ctl.missingData') },
    { color: 'var(--success)', icon: 'wallet', key: 'save', label: t('pkn.ov.stat.potentialSaving'), value: savings.length ? t('pkn.fam.metricPerYear', { amount: fmt(saveSum) }) : '—' },
    { color: alerts.length ? 'var(--error)' : 'var(--muted)', icon: 'alertCircle', key: 'follow', label: t('pkn.ov.stat.needsFollowUp'), value: t('pkn.ov.things', { count: alerts.length }) },
    { color: 'var(--plum-700)', icon: 'checkCircle', key: 'score', label: t('pkn.ov.stat.healthScore'), value: health?.score === null || health?.score === undefined ? '—' : `${Math.round(health.score)} / 100` },
  ];

  return <div className="today">
    <div className="today-stats">
      {stats.map((stat) => <div key={stat.key} className="today-stat" data-help-title={stat.label} data-help={t('helpTopics.' + (stat.key === 'score' || stat.key === 'health' ? 'healthScore' : stat.key === 'save' ? 'scenario' : 'followup') + '.body')}>
        <div className="today-stat-label">
          {stat.icon
            ? <span className="today-stat-ico" style={{ color: stat.color }}><Icon name={stat.icon} size={15} /></span>
            : <span className="today-stat-dot" style={{ background: stat.color }} />}
          {stat.label}
        </div>
        <div className="today-stat-value" style={{ color: stat.color }}>{stat.value}</div>
      </div>)}
    </div>

    {focus && <button type="button" className="today-focus" onClick={() => document.getElementById(`today-task-${topId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
      <span className="today-focus-ico"><Icon name="sparkle" size={20} /></span>
      <div className="today-focus-body">
        <div className="today-focus-kicker">{t('po.todaysFocus')}</div>
        <div className="today-focus-title">{focus.title}</div>
        <div className="today-focus-text">{t('pkn.ov.goToTopTask')}</div>
      </div>
    </button>}

    <div className="today-feed-head">
      <span className="today-feed-title">{t('po.todaysTasks')}</span>
      <span className="today-feed-count">{remaining === 0 ? (insights.length ? t('pkn.ov.allDone') : t('pkn.ov.noneYet')) : t('pkn.ov.remainingCount', { count: remaining })}</span>
    </div>

    <div className="today-feed">
      {visibleCards.map((card) => {
        const severityTone = card.severity === 'critical' ? POV_TODAY_TONE.varsel : card.severity === 'follow-up' ? { bg: 'var(--warning-bg)', color: 'var(--warning)', label: card.severityLabel } : card.severity === 'information' ? POV_TODAY_TONE.innsikt : null;
        const tone = severityTone || POV_TODAY_TONE[card.kind] || POV_TODAY_TONE.mulighet;
        const isTop = card.id === topId;
        const isLeaving = Boolean(leaving[card.id]);
        return <div id={`today-task-${card.id}`} key={card.id} className={`today-card today-card-${card.kind}${isTop ? ' today-card-top-prio' : ''}${isLeaving ? ' leaving' : ''}`} style={{ '--acc': tone.color }}>
          {isTop && <span className="today-ribbon"><Icon name="sparkle" size={12} />{t('po.mostImportantToday')}</span>}
          <div className="today-card-top">
            <button className={`today-check${isLeaving ? ' on' : ''}`} onClick={() => complete(card, 'done')} title={t('po.markDone')} aria-label={t('po.markDone')}><Icon name="check" size={15} /></button>
            <span className="today-card-ico" style={{ background: tone.bg, color: tone.color }}><Icon name={card.icon} size={22} /></span>
            <div className="today-card-hd">
              <span className="today-card-tag" style={{ background: tone.bg, color: tone.color }}>{tone.label}</span>
              <div className="today-card-title">{card.title}</div>
            </div>
            {card.metric && <span className="today-card-metric" style={{ color: tone.color }}>{card.metric}</span>}
          </div>
          <p className="today-card-body">{card.body}</p>
          {(card.evidence?.length > 0 || card.confidence != null) && <details className="today-card-details">
            <summary>{t('pkn.ov.howAdviceCalculated')}</summary>
            <div>{card.confidence != null ? `${t('pkn.for.confidence', { pct: Math.round(card.confidence) })}. ` : ''}{(card.evidence || []).join(' · ')}</div>
          </details>}
          <div className="today-card-actions">
            {(card.actions || []).map((action, index) => <button key={index} className={`today-act${action.primary ? ' primary' : ''}`} onClick={() => act(card, action)}>
              {action.label}{action.primary && <Icon name="arrowRight" size={15} />}
            </button>)}
            <button className="today-act" onClick={() => complete(card, 'snoozed', new Date(Date.now() + 7 * 86400000).toISOString())}>{t('sharedText.adv.status.snooze7')}</button>
          </div>
        </div>;
      })}
      {activeCards.length > 3 && <button type="button" className="today-act" onClick={() => setShowAll((value) => !value)}>{showAll ? t('pkn.ov.showFewer') : t('pkn.ov.seeAll', { count: activeCards.length })}</button>}
      {remaining === 0 && <div className="today-empty">
        <Icon name="checkCircle" size={22} />
        {insights.length === 0
          ? t('pkn.ov.noNewTasks')
          : t('pkn.ov.allTicked')}
      </div>}

      {doneCards.length > 0 && <div className="today-done-wrap">
        <div className="today-done-head"><Icon name="checkCircle" size={14} />{t('pkn.ov.doneToday', { count: doneCards.length })}</div>
        {doneCards.map((card) => <div key={card.id} className="today-done">
          <span className="today-done-ico"><Icon name={done[card.id] === 'done' ? 'checkCircle' : 'x'} size={16} /></span>
          <span className="today-done-txt"><b>{card.title}</b> · {done[card.id] === 'done' ? t('pkn.ov.completed') : t('pkn.ov.hidden')}</span>
          <button className="today-undo" onClick={() => uncomplete(card)}>{t('po.undo')}</button>
        </div>)}
      </div>}
    </div>
  </div>;
}

// ---------------- Oversikt ----------------
function PoOverview({ health = null, hasTransactions = false, name, onAsk, overview = [], recommendations = [], trends = [] }) {
  const Sec = window.PoSecHead;
  const first = String(name || '').split(' ')[0];
  const score = health?.score === null || health?.score === undefined ? null : Math.round(health.score);
  const scoreColor = score >= 80 ? 'var(--success)' : score >= 60 ? 'var(--warning)' : 'var(--error)';
  const delta = health?.scoreDelta;
  const up = Number(delta || 0) >= 0;
  const drivers = health?.drivers || [];
  const cats = health?.items || [];
  const watch = drivers.find((driver) => driver.dir === 'down');
  const watchLabel = watch ? watch.label.charAt(0).toLowerCase() + watch.label.slice(1) : null;
  const groups = [['formue', t('pkn.ov.group.wealth')], ['flyt', t('pkn.ov.group.cashflow')]];

  return <React.Fragment>
    <Card className="po-health">
      <Sec icon="checkCircle" title={t('aii.healthScore')} sub={t('po.healthHeading')}
        help={t('po.healthHelp')} />
      <div className="po-health-grid">
        <div className="po-health-ring">
          <PoScoreRing score={score || 0} />
          <div className="po-health-chips">
            {health?.isPreliminary && <span className="po-hchip po-hchip-muted"><Icon name="alertCircle" size={13} />{t('pkn.ov.preliminary')}</span>}
            {delta === null || delta === undefined
              ? <span className="po-hchip po-hchip-muted"><Icon name="info" size={13} />{t('po.noHistory')}</span>
              : <span className="po-hchip" style={{ background: up ? 'var(--success-bg)' : 'var(--error-bg)', color: up ? 'var(--success)' : 'var(--error)' }}>
                <Icon name={up ? 'trendUp' : 'trendDown'} size={14} />{up ? '+' : ''}{delta} {t('pkn.ov.sinceLastMonth')}
              </span>}
            {health?.scoreBest !== null && health?.scoreBest !== undefined && <span className="po-hchip po-hchip-muted"><Icon name="sparkle" size={13} />{t('pkn.ov.bestEver', { score: health.scoreBest })}</span>}
          </div>
          <PoSpark points={health?.scoreHistory} color={scoreColor} />
          {health?.scoreHistory?.length > 1 && <div className="po-spark-note">{t('pkn.ov.trendMonths', { count: health.scoreHistory.length })}</div>}
        </div>
        <div className="po-health-right">
          <div className="po-drivers-wrap">
            <div className="po-drivers-head">{t('po.whatAffectsScore')}</div>
            <div className="po-drivers">
              {drivers.length === 0
                ? <div className="po-driver"><div className="po-driver-body"><div className="po-driver-label">{t('po.noDrivers')}</div><div className="po-driver-note">{t('po.noDriversHelp')}</div></div></div>
                : drivers.map((driver, index) => <div key={index} className="po-driver">
                  <span className={`po-driver-pts ${driver.dir === 'up' ? 'up' : 'down'}`}><Icon name={driver.dir === 'up' ? 'trendUp' : 'trendDown'} size={13} />{driver.pts}</span>
                  <div className="po-driver-body"><div className="po-driver-label">{driver.label}</div>{driver.note && <div className="po-driver-note">{driver.note}</div>}</div>
                </div>)}
            </div>
          </div>
          <div className="po-health-mascot-wrap"><img className="po-health-mascot" src={(window.__resources && window.__resources.plummeVekst) || 'src/img/plummeVekst.png'} alt={t('pkn.ov.mascotAlt')} /></div>
        </div>
      </div>
      {cats.length > 0 && <div className="aii-cats po-health-cats">
        {cats.map((cat) => <div key={cat.name}>
          <div className="aii-cat-top">
            <span className="aii-cat-dot" style={{ background: POV_STATUS_COLOR[cat.tone] || 'var(--muted-2)' }} />
            <span className="aii-cat-name">{cat.name}</span>
            <span className="aii-cat-val" style={{ color: POV_STATUS_COLOR[cat.tone] || 'var(--muted-2)' }}>{cat.value}</span>
          </div>
          <div className="aii-cat-track"><div className="aii-cat-fill" style={{ background: POV_STATUS_COLOR[cat.tone] || 'var(--muted-2)', width: `${cat.pct}%` }} /></div>
          <div className="aii-cat-note">{cat.note}</div>
        </div>)}
      </div>}
    </Card>

    {trends.length > 0 && <Card>
      <Sec icon="trendUp" title={t('po.last30Days')} sub={t('po.last30DaysSub')} />
      <div className="po-trend-grid">
        {trends.map((trend) => <div key={trend.label} className="po-trend">
          <span className="po-trend-ico"><Icon name={trend.icon} size={17} /></span>
          <div className="po-trend-label">{trend.label}</div>
          <div className="po-trend-val">{trend.value}</div>
          <div className={`po-trend-delta ${trend.good ? 'good' : 'bad'}`}>
            <Icon name={String(trend.delta).trim().charAt(0) === '−' ? 'trendDown' : 'trendUp'} size={13} />{trend.delta}
          </div>
        </div>)}
      </div>
    </Card>}

    <Card>
      <Sec icon="wallet" title={t('po.financialOverview')} sub={t('po.financialOverviewSub')} />
      {groups.map(([key, label]) => <div key={key} className="po-kpi-group">
        <div className="po-kpi-grouphead"><span>{label}</span></div>
        <div className="grid-3" style={{ gap: 14 }}>
          {overview.filter((item) => item.group === key).map((item) => <KPI key={item.label} help={t('helpTopics.' + (item.group === 'formue' ? 'netWealth' : 'cashFlow') + '.body')} tint={item.tint} icon={item.icon} label={item.label} value={item.value} sub={item.sub} trend={item.trend} />)}
        </div>
      </div>)}
      <details className="today-card-details">
        <summary>{t('pkn.ov.howKpisCalculated')}</summary>
        <div>{t('pkn.ov.kpiExplanation')}</div>
      </details>
    </Card>

    {recommendations.length > 0 && <Card>
      <Sec icon="sparkle" title={t('po.recommendations')} sub={t('po.recommendationsSub')}
        right={onAsk ? <button className="aii-hero-pill aii-hero-pill-btn" onClick={onAsk}><Icon name="sparkle" size={15} />{t('po.askMe')}</button> : null} />
      <div className="po-reco3">
        {recommendations.map((item, index) => <button key={index} className="po-reco3-item" onClick={() => item.go && window.__poGoTab?.(item.go)}>
          <span className="po-reco3-rank">{index + 1}</span>
          <span className="po-reco3-ico"><Icon name={item.icon} size={18} /></span>
          <span className="po-reco3-body"><span className="po-reco3-title">{item.title}</span><span className="po-reco3-text">{item.body}</span></span>
          <span className="po-reco3-save">{item.save}</span>
          <Icon name="arrowRight" size={16} className="po-reco3-arrow" />
        </button>)}
      </div>
    </Card>}

    <PlummaSays action={onAsk ? t('pkn.fam.askSuperPlumma') : null} onAction={onAsk}>
      {score === null
        ? <React.Fragment>{t('pkn.ov.noScoreA')}{first ? `, ${first}` : ''}. {t('pkn.ov.noScoreB')}</React.Fragment>
        : <React.Fragment>{t('sharedText.po.yourHealthScoreIs')} <b>{score} {t('sharedText.po.outOf100')}</b>{delta === null || delta === undefined ? '' : <React.Fragment> {t('pkn.ov.andChanged')} <b>{up ? '+' : ''}{delta} {t('pkn.ov.points')}</b> {t('pkn.ov.sinceLastMonth')}</React.Fragment>}.{hasTransactions ? '' : ` ${t('pkn.ov.thinBasis')}`}{watchLabel ? <React.Fragment> {t('pkn.ov.watchExtra')} <b>{watchLabel}</b>.</React.Fragment> : ''}</React.Fragment>}
    </PlummaSays>
  </React.Fragment>;
}

Object.assign(window, { PlummaSays, PoOverview, PoScoreRing, PoSpark, PoToday });
